import React from 'react'
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiPushGetCampaign } from '../../apiService/Core/Apicall'
import CampaignProgress from '../../Components/Push/CampaignProgress'
import { CancelCampaignButton, ExportButton, RetryFailedButton, ReuseCampaignButton } from '../../Components/Push/CampaignActions'
import CounterCards from '../../Components/Push/CounterCards'
import FailureReasons from '../../Components/Push/FailureReasons'
import PushDisabledNotice, { InlineAlert } from '../../Components/Push/PushDisabledNotice'
import PushPageHeader from '../../Components/Push/PushPageHeader'
import PushPanel from '../../Components/Push/PushPanel'
import RecipientTable from '../../Components/Push/RecipientTable'
import StatusPill from '../../Components/Push/StatusPill'
import { useGuardedFetch } from '../../Components/Push/useGuardedFetch'
import { headerBtnSx } from '../../Components/Push/pushStyles'
import {
    ACTIVE_CAMPAIGN_STATUSES, fmtDate, getErrorMessage, isPushDisabled, POLL_INTERVAL_MS, regionName, SEGMENT_OPTIONS,
} from '../../Components/Push/pushUtils'
import type { Campaign } from '../../Components/Push/types'

const Meta: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 10, color: '#6f6f6f', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</Typography>
        <Box sx={{ color: '#e0e0e0', fontSize: 13, overflowWrap: 'anywhere' }}>{children}</Box>
    </Box>
)

const audienceSummary = (c: Campaign) => {
    const a = c.audience
    if (!a) return ['-']
    const parts: string[] = []
    if (a.includeAll) parts.push('All eligible users')
    a.segments?.forEach((s) => parts.push(SEGMENT_OPTIONS.find((o) => o.key === s)?.label ?? s))
    a.groups?.forEach((g) => parts.push(`Group: ${g.name}`))
    a.countries?.forEach((cc) => parts.push(`Country: ${regionName(cc)}`))
    a.states?.forEach((s) => parts.push(`State: ${s.state} (${s.country})`))
    if (a.userIdsCount) parts.push(`${a.userIdsCount} manually selected user${a.userIdsCount === 1 ? '' : 's'}`)
    return parts.length ? parts : ['-']
}

const CampaignDetailView: React.FC<{ id?: string }> = ({ id }) => {
    const navigate = useNavigate()
    const [recipientRefresh, setRecipientRefresh] = React.useState(0)
    const [active, setActive] = React.useState(false)

    const fetcher = React.useCallback((silent: boolean) => ApiPushGetCampaign(id as string, { silent }), [id])
    // polling only while the campaign is FREEZING / QUEUED / RUNNING; the interval is cleared otherwise and on unmount
    const { data, error, loading, refetch } = useGuardedFetch<any>(fetcher, {
        enabled: Boolean(id),
        pollMs: active ? POLL_INTERVAL_MS : 0,
    })
    const campaign: Campaign | undefined = data?.data ?? undefined

    React.useEffect(() => {
        setActive(Boolean(campaign && ACTIVE_CAMPAIGN_STATUSES.includes(campaign.status)))
    }, [campaign])

    const afterAction = () => {
        refetch()
        setRecipientRefresh((k) => k + 1)
    }

    const back = (
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/notifications')} sx={headerBtnSx}>
            Back
        </Button>
    )

    if (isPushDisabled(error)) {
        return (
            <div className="common-layout flex-column">
                <PushPageHeader title="Campaign" actions={back} />
                <PushDisabledNotice />
            </div>
        )
    }

    if (!campaign) {
        return (
            <div className="common-layout flex-column">
                <PushPageHeader title="Campaign" actions={back} />
                {error ? (
                    <InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert>
                ) : (
                    <Box sx={{ textAlign: 'center', p: 4 }}>{(loading || !id) && <CircularProgress sx={{ color: '#ff6a3d' }} />}</Box>
                )}
            </div>
        )
    }

    const canRetry = (campaign.counters?.failed ?? 0) > 0 || (campaign.counters?.devicesFailed ?? 0) > 0

    return (
        <div className="common-layout flex-column">
            <PushPageHeader
                title={campaign.title}
                subtitle={
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                        <StatusPill status={campaign.status} />
                        {campaign.isTest && <Chip size="small" label="Test send" sx={{ color: '#fff', background: 'rgba(255,255,255,0.2)' }} />}
                    </Box>
                }
                actions={
                    <>
                        {back}
                        <ExportButton campaignId={campaign.id} />
                        <RetryFailedButton campaignId={campaign.id} disabled={active || !canRetry || campaign.isTest} onDone={afterAction} />
                        {!campaign.isTest && <ReuseCampaignButton campaignId={campaign.id} />}
                        {active && <CancelCampaignButton campaignId={campaign.id} onDone={afterAction} />}
                    </>
                }
            />

            {error && <Box sx={{ mb: 2 }}><InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert></Box>}

            <Box sx={{ mb: 3 }}>
                <InlineAlert severity="info">
                    &quot;Accepted by Firebase&quot; means the push provider accepted the message for a device. It is not
                    confirmation that the notification was delivered to or seen on the device.
                </InlineAlert>
            </Box>

            <CampaignProgress campaign={campaign} />
            <CounterCards campaign={campaign} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <PushPanel title="Message" icon={<CampaignOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}>
                    <Meta label="Message">
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#e0e0e0' }}>{campaign.title}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#c0c0c0' }}>{campaign.body}</Typography>
                    </Meta>
                    <Meta label="Destination">{campaign.destination || 'Default (open app)'}</Meta>
                    <Meta label="Sender">
                        {campaign.createdBy?.name ?? '-'}
                        {campaign.createdBy?.email ? ` (${campaign.createdBy.email})` : ''}
                    </Meta>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}>
                        <Meta label="Created">{fmtDate(campaign.createdAt)}</Meta>
                        <Meta label="Audience frozen">{fmtDate(campaign.freezeCompletedAt)}</Meta>
                        <Meta label="Started">{fmtDate(campaign.startedAt)}</Meta>
                        <Meta label="Completed">{fmtDate(campaign.completedAt)}</Meta>
                    </Box>
                </PushPanel>
                <PushPanel title="Audience filters used" icon={<FilterAltOutlinedIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {audienceSummary(campaign).map((s, i) => (
                            <Chip key={`${s}-${i}`} size="small" label={s} sx={{ background: '#241815', color: '#e0e0e0', border: '1px solid #ff6a3d40' }} />
                        ))}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: '#5a5a5a', mt: 1.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                        Union of all sources, duplicates removed, frozen at send time.
                    </Typography>
                </PushPanel>
            </Box>

            <FailureReasons campaign={campaign} />

            <RecipientTable campaignId={campaign.id} polling={active} refreshKey={recipientRefresh} />
        </div>
    )
}

// keyed by id so navigating between campaigns never shows the previous campaign's data
const CampaignDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    return <CampaignDetailView key={id} id={id} />
}

export default CampaignDetail
